import { Card } from '@shared/components/ui';
import {
  Calendar,
  CheckCircle,
  Database,
  FileText,
  HardDrive,
  HardDrive as StorageIcon,
  Archive,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PreviewProps {
  data: {
    name?: string;
    type: 'POSTGRESQL' | 'FILES';
    agent_id?: string;
    host?: string;
    port?: number;
    username?: string;
    database?: string;
    databases?: string[];
    usePgDumpAll?: boolean;
    backupGlobalsOnly?: boolean;
    path?: string;
    paths?: string[];
    useVSS?: boolean; // Windows Volume Shadow Copy Service
    schedule_type: string;
    time?: string;
    day_of_week?: number;
    day_of_month?: number;
    interval_minutes?: number;
    cron_expression?: string;
    storage_destination_id?: string;
    storage_destination_name?: string;
    agent_name?: string;
    retention_policy?: {
      keep_daily?: number;
      keep_weekly?: number;
      keep_monthly?: number;
      keep_last?: number;
    };
  };
  isEditing?: boolean;
  onConfirm: () => void;
  onBack: () => void;
}

export function Preview({
  data,
  isEditing = false,
  onConfirm: _onConfirm,
  onBack: _onBack,
}: PreviewProps) {
  const { t } = useTranslation('backups');

  const getScheduleSummary = () => {
    switch (data.schedule_type) {
      case 'DAILY':
        return t('wizard.preview.schedule.daily', { time: data.time });
      case 'WEEKLY': {
        const days = [
          t('wizard.schedule.days.0'),
          t('wizard.schedule.days.1'),
          t('wizard.schedule.days.2'),
          t('wizard.schedule.days.3'),
          t('wizard.schedule.days.4'),
          t('wizard.schedule.days.5'),
          t('wizard.schedule.days.6'),
        ];
        return t('wizard.preview.schedule.weekly', {
          day: days[data.day_of_week || 0],
          time: data.time,
        });
      }
      case 'MONTHLY':
        return t('wizard.preview.schedule.monthly', { day: data.day_of_month, time: data.time });
      case 'MINUTELY':
        return t('wizard.preview.schedule.minutely', { minutes: data.interval_minutes || 15 });
      case 'CUSTOM':
        return t('wizard.preview.schedule.custom', { cron: data.cron_expression });
      default:
        return t('wizard.preview.schedule.notConfigured');
    }
  };

  const getSourceSummary = () => {
    if (data.type === 'POSTGRESQL') {
      if (data.usePgDumpAll) {
        return `${data.username}@${data.host}:${data.port} (${t('wizard.preview.source.allDatabases')})`;
      }
      if (data.backupGlobalsOnly) {
        return `${data.username}@${data.host}:${data.port} (${t('wizard.preview.source.globalsOnly')})`;
      }
      // Single database selection
      if (data.database) {
        return `${data.username}@${data.host}:${data.port} (${t('wizard.preview.source.database', { database: data.database })})`;
      }
      return `${data.username}@${data.host}:${data.port}`;
    } else {
      const paths = data.paths?.filter(Boolean) ?? [];
      const path =
        paths.length > 1
          ? t('wizard.preview.source.pathsCount', { count: paths.length })
          : paths[0] || data.path || t('wizard.preview.schedule.notConfigured');
      if (data.useVSS) {
        return `${path} (${t('wizard.preview.source.vssEnabled')})`;
      }
      return path;
    }
  };

  const getRetentionSummary = () => {
    const rp = data.retention_policy;
    if (!rp) return t('wizard.preview.retention.default');
    const parts = [];
    if (rp.keep_last) parts.push(t('wizard.preview.retention.lastN', { n: rp.keep_last }));
    if (rp.keep_daily) parts.push(t('wizard.preview.retention.daily', { n: rp.keep_daily }));
    if (rp.keep_weekly) parts.push(t('wizard.preview.retention.weekly', { n: rp.keep_weekly }));
    if (rp.keep_monthly) parts.push(t('wizard.preview.retention.monthly', { n: rp.keep_monthly }));
    return parts.length > 0 ? parts.join(', ') : t('wizard.preview.retention.none');
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-state-ok-bg-light dark:bg-state-ok-bg-dark border border-state-ok-light/20 dark:border-state-ok-dark/20 rounded-2xl flex items-start space-x-3">
        <CheckCircle className="text-state-ok-light dark:text-state-ok-dark shrink-0" size={20} />
        <p className="text-xs font-medium text-state-ok-light dark:text-state-ok-dark leading-relaxed">
          {t(isEditing ? 'wizard.preview.reviewInfoEdit' : 'wizard.preview.reviewInfo')}
        </p>
      </div>

      <Card className="p-8 border-surface-border-light dark:border-surface-border-dark shadow-sm">
        <div className="space-y-8">
          {/* Name Section */}
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-state-info-bg-light dark:bg-state-info-bg-dark rounded-lg flex items-center justify-center">
              <FileText
                size={20}
                className="text-state-info-light dark:text-state-info-dark"
                strokeWidth={2.5}
              />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-1">
                {t('wizard.preview.sections.name')}
              </p>
              <p className="text-sm font-bold text-slate-900 dark:text-white break-all">
                {data.name || t('wizard.preview.unnamed')}
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-accent-soft-light dark:bg-accent-soft-dark rounded-lg flex items-center justify-center">
              {data.type === 'POSTGRESQL' ? (
                <Database
                  size={20}
                  className="text-brand-600 dark:text-brand-400"
                  strokeWidth={2.5}
                />
              ) : (
                <HardDrive
                  size={20}
                  className="text-brand-600 dark:text-brand-400"
                  strokeWidth={2.5}
                />
              )}
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-1">
                {t('wizard.preview.sections.source')} (
                {data.type === 'FILES'
                  ? t('wizard.source.filesystem')
                  : t('wizard.source.postgresql')}
                )
              </p>
              <p className="text-sm font-bold text-slate-900 dark:text-white break-all">
                {getSourceSummary()}
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-state-warn-bg-light dark:bg-state-warn-bg-dark rounded-lg flex items-center justify-center">
              <Calendar
                size={20}
                className="text-state-warn-light dark:text-state-warn-dark"
                strokeWidth={2.5}
              />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-1">
                {t('wizard.preview.sections.schedule')}
              </p>
              <p className="text-sm font-bold text-slate-900 dark:text-white">
                {getScheduleSummary()}
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-state-ok-bg-light dark:bg-state-ok-bg-dark rounded-lg flex items-center justify-center">
              <StorageIcon
                size={20}
                className="text-state-ok-light dark:text-state-ok-dark"
                strokeWidth={2.5}
              />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-1">
                {t('wizard.preview.sections.storage')}
              </p>
              <p className="text-sm font-bold text-slate-900 dark:text-white">
                {data.storage_destination_name || t('wizard.preview.schedule.notConfigured')}
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-10 h-10 bg-surface-2-light dark:bg-surface-2-dark rounded-lg flex items-center justify-center">
              <Archive
                size={20}
                className="text-text-muted-light dark:text-text-muted-dark"
                strokeWidth={2.5}
              />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-1">
                {t('wizard.preview.sections.retention')}
              </p>
              <p className="text-sm font-bold text-slate-900 dark:text-white">
                {getRetentionSummary()}
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
